import { EarningsItemBase } from '@api/api/earnings';
import {
  EarningsInsight,
  EarningsInsightRecapResponse,
} from '@api/api/earnings-insights';
import { TextSection } from '@terminal/components/text-section/TextSection';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { EarningsInsightPill } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-pill/EarningsInsightPill';
import { PublishedDate } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/published-date/PublishedDate';
import { useTranslation } from 'react-i18next';

import { EarningsHighlightsTable } from '../earnings-highlights-table/EarningsHighlightsTable';
import styles from './RecapOverview.module.scss';
import { splitIntoBullets } from './utils';

interface RecapOverviewProps {
  title: string;
  activeEntityTag: string;
  earningsData: EarningsItemBase;
  recapData: EarningsInsightRecapResponse;
  recapEarningsInsight?: EarningsInsight;
}

export const RecapOverview = ({
  title,
  activeEntityTag,
  earningsData,
  recapData,
  recapEarningsInsight,
}: RecapOverviewProps) => {
  const { t } = useTranslation(['earnings']);

  const keyMetrics = splitIntoBullets(recapData.key_metrics);
  const guidanceUpdate = splitIntoBullets(recapData.guidance_update);

  return (
    <div className={styles.container}>
      <div className={styles.pillWrapper}>
        {recapEarningsInsight && (
          <EarningsInsightPill
            entityTag={activeEntityTag}
            earningsInsight={recapEarningsInsight}
            isClickable={false}
            isPreview={false}
            source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
          />
        )}
        <PublishedDate
          publishedAt={recapData.created_at}
          updatedAt={recapData.updated_at}
          isPreview={false}
          fiscalYear={earningsData.fiscal_year}
          fiscalPeriod={earningsData.fiscal_period}
          releasedAt={earningsData.reporting_date}
        />
      </div>
      <p className={styles.title}>{title}</p>
      <EarningsHighlightsTable earningsData={earningsData} />
      <div className={styles.keyMetricsWrapper}>
        <TextSection
          title={t('earnings:earningsInsight.drawer.earningsRecap.keyMetrics')}
          variant={'secondary'}
          useBulletPoints={keyMetrics.length > 1}
          textContent={keyMetrics}
        />
      </div>
      <TextSection
        title={t(
          'earnings:earningsInsight.drawer.earningsRecap.guidanceUpdate'
        )}
        variant={'secondary'}
        useBulletPoints={guidanceUpdate.length > 1}
        textContent={guidanceUpdate}
      />
    </div>
  );
};
